import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  CircleAlert,
  Clock3,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { payoutsService } from '@/services/payouts.service';
import type {
  AccrualsSummaryView,
  CommissionPayoutStatus,
  CommissionPayoutView,
  ConnectStatusBucket,
  ConnectStatusView,
} from '@/types/api';

/**
 * AffiliatePayoutsCard — paid-user-only Stripe Connect status surface +
 * payout history. Renders below the existing `AffiliateSection` stats
 * card on the Account page.
 *
 * State machine matches `ConnectStatusBucket` from the server:
 *   * notStarted   → "Set up payouts" CTA.
 *   * pending      → "Complete your info" with a fresh onboarding link.
 *   * verifying    → "Stripe is reviewing your info."
 *   * ready        → "Connected to bank ••••XXXX" + Manage link.
 *   * actionNeeded → red banner listing currently-due fields + Finish setup link.
 *
 * Three independent fetches (status, summary, payouts) — they're
 * surfaced as separate sections, so a slow Stripe round-trip on status
 * doesn't gate the headline numbers from rendering.
 */
export function AffiliatePayoutsCard() {
  const [status, setStatus] = useState<ConnectStatusView | null>(null);
  const [summary, setSummary] = useState<AccrualsSummaryView | null>(null);
  const [payouts, setPayouts] = useState<CommissionPayoutView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refresh-all helper — used on mount, on action completion, and on
  // ?connect=return arrival (handled by the parent page; we re-read on
  // the next mount cycle).
  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, sum, list] = await Promise.all([
        payoutsService.getStatus(),
        payoutsService.getSummary(),
        payoutsService.listPayouts({ take: 10 }),
      ]);
      setStatus(s);
      setSummary(sum);
      setPayouts(list.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payouts data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartOnboarding = async () => {
    setActionLoading(true);
    try {
      const { url } = await payoutsService.startOnboarding();
      // Redirect to Stripe immediately — the link expires in ~5 min.
      window.location.assign(url);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Couldn't open onboarding. Try again.";
      toast.error(message);
      setActionLoading(false);
    }
  };

  const handleManage = async () => {
    setActionLoading(true);
    try {
      const { url } = await payoutsService.getUpdateLink();
      window.location.assign(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Couldn't open Stripe. Try again.";
      toast.error(message);
      setActionLoading(false);
    }
  };

  return (
    <section className="rounded-2xl border border-or-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-or-teal-600" aria-hidden />
            <div className="font-display text-[16px] font-bold text-or-slate-900">
              Payouts
            </div>
          </div>
          <p className="mt-1 text-[13px] text-or-slate-600">
            Connect a bank account through Stripe to receive your monthly commission
            payouts. We never see your bank details — Stripe handles verification and
            tax forms.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-6">
          <InlineLoader label="Loading payouts…" />
        </div>
      ) : error ? (
        <div className="mt-6 rounded-xl border border-or-rose-200 bg-or-rose-50 px-4 py-3 text-[13px] text-or-rose-700">
          {error}
        </div>
      ) : (
        <>
          {summary && (
            <SummaryTiles summary={summary} pending={status?.bucket !== 'ready'} />
          )}

          {status && (
            <StatusPanel
              status={status}
              actionLoading={actionLoading}
              onStart={handleStartOnboarding}
              onManage={handleManage}
            />
          )}

          {payouts !== null && <PayoutsHistory items={payouts} />}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Summary tiles — pending balance + lifetime numbers.
// ---------------------------------------------------------------------------
function SummaryTiles({
  summary,
  pending,
}: {
  summary: AccrualsSummaryView;
  pending: boolean;
}) {
  return (
    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Tile
        label="Pending balance"
        amountCents={summary.pendingBalanceCents}
        currency={summary.currency}
        hint={
          pending
            ? 'Connect a bank account to receive this on the next payout'
            : 'Will be sent on the next monthly payout'
        }
        accent
      />
      <Tile
        label="Lifetime earned"
        amountCents={summary.lifetimeEarnedCents}
        currency={summary.currency}
        hint="Total commissions you've earned"
      />
      <Tile
        label="Lifetime paid out"
        amountCents={summary.lifetimePaidOutCents}
        currency={summary.currency}
        hint={
          summary.lastPaidAt
            ? `Last paid ${formatDate(summary.lastPaidAt)}`
            : 'No payouts yet'
        }
      />
    </div>
  );
}

function Tile({
  label,
  amountCents,
  currency,
  hint,
  accent = false,
}: {
  label: string;
  amountCents: number;
  currency: string;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? 'rounded-xl border border-or-teal-200 bg-or-teal-50/50 px-4 py-4'
          : 'rounded-xl border border-or-slate-100 bg-or-cream-50 px-4 py-4'
      }
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-or-slate-500">
        {label}
      </div>
      <div className="mt-1 font-display text-[24px] font-bold text-or-slate-900">
        {formatMoney(amountCents, currency)}
      </div>
      <div className="text-[12px] text-or-slate-600">{hint}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status panel — variant per bucket.
// ---------------------------------------------------------------------------
function StatusPanel({
  status,
  actionLoading,
  onStart,
  onManage,
}: {
  status: ConnectStatusView;
  actionLoading: boolean;
  onStart: () => void;
  onManage: () => void;
}) {
  switch (status.bucket) {
    case 'notStarted':
      return (
        <div className="mt-5 flex flex-col gap-3 rounded-xl border border-or-slate-200 bg-or-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-display text-[14px] font-semibold text-or-slate-900">
              Set up payouts
            </div>
            <p className="text-[13px] text-or-slate-600">
              You'll be redirected to Stripe to verify your identity and add a bank account.
              Takes about 5 minutes.
            </p>
          </div>
          <Button onClick={onStart} disabled={actionLoading}>
            <Banknote className="h-4 w-4" aria-hidden />
            Set up with Stripe
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      );

    case 'pending':
      return (
        <div className="mt-5 flex flex-col gap-3 rounded-xl border border-or-amber-200 bg-or-amber-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <Clock3 className="mt-0.5 h-4 w-4 flex-shrink-0 text-or-amber-700" aria-hidden />
            <div>
              <div className="font-display text-[14px] font-semibold text-or-amber-900">
                Finish setting up payouts
              </div>
              <p className="text-[13px] text-or-amber-800">
                You started onboarding but didn't finish. Resume to add your details and
                bank account.
              </p>
            </div>
          </div>
          <Button onClick={onStart} disabled={actionLoading}>
            Resume setup
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      );

    case 'verifying':
      return (
        <div className="mt-5 rounded-xl border border-or-slate-200 bg-or-slate-50 px-4 py-4">
          <div className="flex items-start gap-2">
            <Clock3 className="mt-0.5 h-4 w-4 flex-shrink-0 text-or-slate-600" aria-hidden />
            <div>
              <div className="font-display text-[14px] font-semibold text-or-slate-900">
                Stripe is reviewing your info
              </div>
              <p className="text-[13px] text-or-slate-600">
                This usually takes a few minutes. We'll start sending your monthly commission
                as soon as it's approved.
              </p>
            </div>
          </div>
        </div>
      );

    case 'ready':
      return (
        <div className="mt-5 flex flex-col gap-3 rounded-xl border border-or-emerald-200 bg-or-emerald-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-or-emerald-700" aria-hidden />
            <div>
              <div className="font-display text-[14px] font-semibold text-or-emerald-900">
                Connected{status.bankLast4 ? ` — bank ••••${status.bankLast4}` : ''}
              </div>
              <p className="text-[13px] text-or-emerald-800">
                Your monthly commission will be sent here on the 1st of each month.
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={onManage} disabled={actionLoading}>
            Manage in Stripe
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      );

    case 'actionNeeded':
      return (
        <div className="mt-5 flex flex-col gap-3 rounded-xl border border-or-rose-200 bg-or-rose-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-or-rose-700" aria-hidden />
            <div>
              <div className="font-display text-[14px] font-semibold text-or-rose-900">
                Action needed
              </div>
              <p className="text-[13px] text-or-rose-800">
                Stripe needs more information before they can send payouts. Click below to
                review.
              </p>
              {status.currentlyDue.length > 0 && (
                <ul className="mt-1 list-inside list-disc text-[12px] text-or-rose-700">
                  {status.currentlyDue.slice(0, 4).map((req) => (
                    <li key={req}>{humanizeRequirement(req)}</li>
                  ))}
                  {status.currentlyDue.length > 4 ? (
                    <li>and {status.currentlyDue.length - 4} more…</li>
                  ) : null}
                </ul>
              )}
            </div>
          </div>
          <Button onClick={onManage} disabled={actionLoading}>
            Finish setup
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// Payouts history — last 10 rows.
// ---------------------------------------------------------------------------
function PayoutsHistory({ items }: { items: CommissionPayoutView[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="mt-6">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-or-slate-500">
        Recent payouts
      </div>
      <ul className="mt-2 divide-y divide-or-slate-100 rounded-xl border border-or-slate-100">
        {items.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 px-4 py-3 text-[13px]"
          >
            <div>
              <div className="font-medium text-or-slate-800">
                {formatMoney(p.amountCents, p.currency)}
              </div>
              <div className="text-[12px] text-or-slate-500">{formatDate(p.createdAt)}</div>
            </div>
            <StatusChip status={p.status} reason={p.failureReason} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusChip({
  status,
  reason,
}: {
  status: CommissionPayoutStatus;
  reason: string | null;
}) {
  switch (status) {
    case 'paid':
      return <Chip variant="live">Paid</Chip>;
    case 'pending':
      return <Chip variant="pending">Processing</Chip>;
    case 'failed':
      return (
        <span className="flex items-center gap-1 text-[12px] text-or-rose-700" title={reason ?? undefined}>
          <CircleAlert className="h-3 w-3" aria-hidden />
          Failed
        </span>
      );
    case 'reversed':
      return (
        <span className="text-[12px] text-or-slate-500" title={reason ?? undefined}>
          Reversed
        </span>
      );
  }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Stripe `requirements.currently_due` field names are dot-separated
 * machine identifiers like `individual.dob.day` or `external_account`.
 * We surface a friendlier label rather than the raw key — anything we
 * don't recognise falls back to the key itself.
 */
function humanizeRequirement(key: string): string {
  const labels: Record<string, string> = {
    'individual.dob.day': 'Date of birth',
    'individual.dob.month': 'Date of birth',
    'individual.dob.year': 'Date of birth',
    'individual.first_name': 'First name',
    'individual.last_name': 'Last name',
    'individual.ssn_last_4': 'Last 4 of SSN',
    'individual.id_number': 'Full SSN or EIN',
    'individual.address.line1': 'Home address',
    'individual.address.postal_code': 'ZIP code',
    'individual.phone': 'Phone number',
    'individual.email': 'Email address',
    external_account: 'Bank account',
    tos_acceptance: 'Stripe terms acceptance',
  };
  return labels[key] ?? key.replace(/_/g, ' ');
}
